import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const { checkSubscription, subscribed, loading } = useSubscription();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Track Google Ads conversion
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-10810604019/UlpHCOvuxYsZEPPz8qIo',
        'transaction_id': ''
      });
    }

    // Check subscription status multiple times to ensure Stripe webhook has processed
    const checkStatus = async () => {
      setChecking(true);
      
      // Check immediately
      await checkSubscription();
      
      // Check again after 2 seconds (webhook may take time)
      setTimeout(async () => {
        await checkSubscription();
        setChecking(false);
      }, 2000);
    };

    checkStatus();
  }, [checkSubscription]);

  const handleContinue = () => {
    navigate("/dashboard");
  };

  if (checking || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Processing your subscription...</h2>
          <p className="text-muted-foreground">This will only take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        
        <h1 className="text-3xl font-bold mb-4">
          {subscribed ? "Welcome to Premium!" : "Payment Received!"}
        </h1>
        
        <p className="text-muted-foreground mb-8">
          {subscribed
            ? "Your subscription is now active. Enjoy unlimited thumbnail generations and all premium features!"
            : "Your payment was successful. Your subscription will be activated shortly."
          }
        </p>

        <div className="p-6 rounded-2xl bg-card border border-border mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-brand-purple" />
            <span className="font-semibold">Premium Benefits</span>
          </div>
          <ul className="text-sm text-muted-foreground space-y-2 text-left">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Unlimited thumbnail generations
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              HD & 4K export quality
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Priority generation speed
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              All styles and emotions
            </li>
          </ul>
        </div>

        <Button variant="hero" size="xl" className="w-full" onClick={handleContinue}>
          <Sparkles className="w-5 h-5" />
          Start Creating Thumbnails
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
